import React, { useRef, useState } from 'react';
import { ImagePlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { getBackendBaseUrl } from '../../utils/apiBase';
import { humanizeError } from '../../utils/errorHandling';
import { uploadTreeCover, validateTreeCoverFile, CUSTOMER_IMAGE_ACCEPT } from '../../utils/treeCoverUpload';
import { ArchiveCoverFallback } from './ArchiveCoverFallback';
import { ArchiveCoverImage } from './ArchiveCoverImage';
import { normalizeCoverDisplay } from '../../utils/coverDisplay';

const BACKEND_URL = getBackendBaseUrl();

/**
 * Wide archive banner — calm family cover, not a social feed header.
 * Upload/replace/remove live in Tree Settings (TreeCoverManager). Hero is display-only by default.
 * @param {'card'|'hero'} variant
 */
export function ArchiveTreeCover({
  coverImageUrl,
  alt = 'Family archive cover',
  variant = 'card',
  className,
  treeName,
  canEdit = false,
  treeId,
  onCoverUpdated,
  enableHeroQuickActions = false,
  coverDisplay,
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const src = coverImageUrl ? resolveAssetUrl(BACKEND_URL, coverImageUrl) : null;
  const showHeroActions =
    enableHeroQuickActions &&
    variant === 'hero' &&
    canEdit &&
    treeId &&
    typeof onCoverUpdated === 'function';

  const aspectClass =
    variant === 'hero'
      ? 'aspect-[21/6] max-h-[108px] sm:max-h-[132px] lg:max-h-[156px]'
      : 'aspect-[16/5]';

  const openFilePicker = () => {
    if (uploading) return;
    fileInputRef.current?.click();
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validation = validateTreeCoverFile(file);
    if (!validation.ok) {
      toast.error(validation.message);
      return;
    }

    setUploading(true);
    try {
      const result = await uploadTreeCover(treeId, file);
      toast.success('Archive cover saved.');
      onCoverUpdated?.(result?.cover_image_url ?? null);
    } catch (error) {
      toast.error(humanizeError(error) || 'Unable to save the cover image.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div
      className={cn(
        'relative w-full overflow-hidden rounded-t-2xl border-b border-[hsl(38_14%_88%)]/90',
        variant === 'hero' &&
          'rounded-b-none border border-[hsl(38_14%_86%)]/95 shadow-[inset_0_1px_0_hsl(42_40%_100%/0.45)]',
        variant === 'card' && 'rounded-b-none',
        className
      )}
      data-testid="archive-tree-cover"
    >
      {showHeroActions ? (
        <input
          ref={fileInputRef}
          type="file"
          accept={CUSTOMER_IMAGE_ACCEPT}
          className="hidden"
          onChange={handleFile}
          data-testid="hero-cover-file-input"
        />
      ) : null}

      <div className={cn('relative w-full', aspectClass)}>
        {src ? (
          <ArchiveCoverImage
            src={src}
            alt={alt}
            coverDisplay={normalizeCoverDisplay(coverDisplay)}
            className="absolute inset-0 h-full w-full"
          />
        ) : (
          <ArchiveCoverFallback
            className="absolute inset-0 h-full w-full"
            compact={variant === 'card'}
            treeName={treeName}
            showEmptyState={variant === 'hero'}
            canUpload={showHeroActions}
            onUploadClick={openFilePicker}
            uploading={uploading}
          />
        )}
        {src && showHeroActions ? (
          <div className="absolute bottom-3 right-3 z-10 sm:bottom-4 sm:right-4">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-full border border-white/35 bg-[hsl(28_12%_16%/_0.42)] px-3 py-1.5 text-xs font-medium text-[hsl(42_30%_96%)] shadow-sm backdrop-blur-sm transition-colors hover:bg-[hsl(28_12%_16%/_0.58)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 disabled:opacity-60"
              disabled={uploading}
              onClick={openFilePicker}
              data-testid="hero-cover-replace-btn"
            >
              {uploading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden />
              ) : (
                <ImagePlus className="h-3.5 w-3.5" aria-hidden />
              )}
              Replace cover
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
